import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format cents to dollars with currency symbol
 */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Format mileage with commas and "mi" suffix
 */
export function formatMileage(miles: number): string {
  return `${new Intl.NumberFormat("en-US").format(miles)} mi`;
}

/**
 * Format a vehicle title from its parts
 */
export function formatVehicleTitle(
  year: number,
  make: string,
  model: string,
  trim?: string,
): string {
  const base = `${year} ${make} ${model}`;
  return trim ? `${base} ${trim}` : base;
}

/**
 * Format a date string
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  }).format(new Date(date));
}

/**
 * Format a phone number
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

/**
 * Calculate monthly payment estimate
 */
export function calculateMonthlyPayment(
  priceCents: number,
  downPaymentCents: number,
  aprPercent: number,
  termMonths: number,
): number {
  const principal = (priceCents - downPaymentCents) / 100;
  const monthlyRate = aprPercent / 100 / 12;

  if (monthlyRate === 0) {
    return Math.round((principal / termMonths) * 100);
  }

  const payment =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) /
    (Math.pow(1 + monthlyRate, termMonths) - 1);

  return Math.round(payment * 100);
}
